import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { ChatChannelsCard, type TelegramLinks } from '../components/ChatChannels.js';
import { ChatIcon, HookIcon, MailIcon } from '../components/icons.js';
import {
  Button,
  Card,
  Checkbox,
  LoadError,
  Loading,
  Notice,
  SelectField,
  TextField,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { LANGUAGES } from '../i18n.js';

type Prefs = Schemas['AlertPreferences'];
type Channel = Schemas['AlertChannel'];
const MIN_DOWNTIME = [0, 1, 2, 5, 10] as const;
const REMINDERS = [0, 6, 12, 24, 48] as const;

function Preferences() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const prefs = useQuery({
    queryKey: ['alert-preferences'],
    queryFn: () => unwrap(api.GET('/account/alert-preferences')),
  });
  const [draft, setDraft] = useState<Prefs>();
  // The e-mail language follows the app until it is chosen here (PUT with emailLocale): only a
  // choice made in the list is sent, so saving the switches keeps it following.
  const [localeChosen, setLocaleChosen] = useState(false);
  useEffect(() => {
    if (prefs.data) setDraft(prefs.data);
  }, [prefs.data]);
  const save = useMutation({
    mutationFn: (body: Prefs) => unwrap(api.PUT('/account/alert-preferences', { body })),
    onSuccess: (data) => client.setQueryData(['alert-preferences'], data),
  });
  if (prefs.isPending || !draft)
    return prefs.isError ? (
      <LoadError message={errorMessage(prefs.error, t)} onRetry={() => void prefs.refetch()} />
    ) : (
      <Loading />
    );
  const set = <K extends keyof Prefs>(key: K, value: Prefs[K]) =>
    setDraft({ ...draft, [key]: value });
  const switches = [
    'downAlert',
    'recoveryAlert',
    'degradedAlert',
    'goodNewsAlert',
    'weeklyDigest',
    'monthlyReport',
  ] as const;
  return (
    <Card>
      <h2>{t('notifications.prefsTitle')}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const { emailLocale, ...rest } = draft;
          save.mutate(localeChosen && emailLocale ? { ...rest, emailLocale } : rest);
        }}
      >
        <fieldset className="checks">
          <legend className="sr-only">{t('notifications.prefsTitle')}</legend>
          {switches.map((key) => (
            <Checkbox
              key={key}
              label={t(`notifications.${key}`)}
              checked={draft[key]}
              onChange={(e) => set(key, e.target.checked)}
            />
          ))}
        </fieldset>
        <hr />
        <div className="fields3">
          <SelectField
            label={t('notifications.minDowntime')}
            value={draft.minDowntimeMinutes}
            onChange={(e) =>
              set('minDowntimeMinutes', Number(e.target.value) as Prefs['minDowntimeMinutes'])
            }
          >
            {MIN_DOWNTIME.map((m) => (
              <option key={m} value={m}>
                {t(`notifications.minDowntimeOptions.${m}`)}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t('notifications.reminder')}
            value={draft.downReminderHours}
            onChange={(e) =>
              set('downReminderHours', Number(e.target.value) as Prefs['downReminderHours'])
            }
          >
            {REMINDERS.map((h) => (
              <option key={h} value={h}>
                {t(`notifications.reminderOptions.${h}`)}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t('notifications.emailLocale')}
            hint={t('notifications.emailLocaleHint')}
            value={draft.emailLocale ?? 'de'}
            onChange={(e) => {
              setLocaleChosen(true);
              set('emailLocale', e.target.value as Prefs['emailLocale']);
            }}
          >
            {LANGUAGES.map((l) => (
              <option key={l} value={l} lang={l}>
                {t(`lang.${l}`)}
              </option>
            ))}
          </SelectField>
        </div>
        {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
        <div className="acts">
          <Button type="submit" busy={save.isPending}>
            {t('app.save')}
          </Button>
          {save.isSuccess ? (
            <span role="status" className="hint">
              {t('app.saved')}
            </span>
          ) : null}
        </div>
      </form>
    </Card>
  );
}

const ICONS = { email: MailIcon, telegram: ChatIcon, slack: ChatIcon, webhook: HookIcon };

/** One recipient: the address or chat, its state and what can be done with it. */
function Recipient({
  channel: c,
  telegram,
  onAct,
  onRelink,
  relinking,
}: {
  channel: Channel;
  /** Whether the plan has Telegram: its links show in the Telegram box of the chat card. */
  telegram: boolean;
  onAct: (action: 'test' | 'remove' | 'toggle', id: string) => void;
  onRelink: (id: string) => void;
  relinking: boolean;
}) {
  const { t } = useTranslation();
  // The row's controls are about its address: "Entfernen" alone does not say which (review of SP-UX10.6a).
  const who = useId();
  const Icon = ICONS[c.type];
  const waiting = c.type === 'telegram' && !c.verified;
  return (
    <li>
      <span className="ico">
        <Icon />
      </span>
      <div className="who" id={who}>
        <b>
          {c.type === 'email' ? null : `${t(`notifications.types.${c.type}`)} · `}
          {waiting ? <span>{t('chat.waitingShort')}</span> : c.target}
          {c.isOwner ? <span> ({t('notifications.you')})</span> : null}
        </b>
        {waiting && telegram ? (
          // A new link, shown in the Telegram box of the chat card (notifications.html: "Link anzeigen").
          <button
            type="button"
            className="relink"
            disabled={relinking}
            aria-busy={relinking || undefined}
            onClick={() => onRelink(c.id)}
          >
            {t('chat.showLink')}
          </button>
        ) : (
          <span>
            {waiting
              ? null
              : c.verified
                ? t(c.type === 'email' ? 'notifications.confirmed' : 'notifications.chatOnly')
                : t('notifications.pending')}
          </span>
        )}
      </div>
      <div className="acts">
        {waiting ? null : (
          <Checkbox
            label={t('notifications.active')}
            aria-describedby={who}
            checked={c.enabled}
            onChange={() => onAct('toggle', c.id)}
          />
        )}
        {c.verified && c.enabled ? (
          <Button
            variant="ghost"
            size="sm"
            aria-describedby={who}
            onClick={() => onAct('test', c.id)}
          >
            {t('notifications.test')}
          </Button>
        ) : null}
        {c.isOwner ? null : (
          <Button
            variant="ghost"
            size="sm"
            aria-describedby={who}
            onClick={() => {
              if (window.confirm(t('notifications.removeConfirm', { target: c.target })))
                onAct('remove', c.id);
            }}
          >
            {t('notifications.remove')}
          </Button>
        )}
      </div>
    </li>
  );
}

function Recipients({ onLinks }: { onLinks: (links: TelegramLinks) => void }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const channels = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => unwrap(api.GET('/alert-channels')),
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['alert-channels'] });
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<{
    kind: 'success' | 'error';
    title?: string;
    text: string;
  }>();
  const add = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/alert-channels', { body: { type: 'email', target: email } })),
    onSuccess: (c) => {
      setMessage({
        kind: 'success',
        title: t('notifications.addedTitle'),
        text: t('notifications.added', { target: c.target }),
      });
      setEmail('');
      void refresh();
    },
    onError: (error) =>
      setMessage({
        kind: 'error',
        text:
          error instanceof ApiError && error.code === 'PLAN_LIMIT_REACHED'
            ? t('notifications.limitReached', { count: error.problem.limit?.max ?? 1 })
            : errorMessage(error, t),
      }),
  });
  /** Why the plan refuses to switch a recipient back on (SP-A2.54). */
  const switchOnRefusal = (error: unknown) => {
    if (!(error instanceof ApiError) || error.code !== 'PLAN_LIMIT_REACHED') return undefined;
    const limit = error.problem.limit;
    if (limit?.name === 'alertRecipients')
      return t('notifications.limitReached', { count: limit.max ?? 1 });
    if (limit?.name === 'webhookAlerts') return t('chat.webhookPlan');
    if (limit?.name === 'chatAlerts') return t('chat.planNeeded');
    return undefined;
  };
  const act = useMutation({
    mutationFn: async ({ action, id }: { action: 'test' | 'remove' | 'toggle'; id: string }) => {
      if (action === 'test')
        return unwrap(
          api.POST('/alert-channels/{channelId}/test', { params: { path: { channelId: id } } }),
        );
      if (action === 'remove')
        return unwrap(
          api.DELETE('/alert-channels/{channelId}', { params: { path: { channelId: id } } }),
        );
      const current = channels.data?.items.find((c) => c.id === id);
      return unwrap(
        api.PATCH('/alert-channels/{channelId}', {
          params: { path: { channelId: id } },
          body: { enabled: !current?.enabled },
        }),
      );
    },
    onSuccess: (_d, v) => {
      const target = channels.data?.items.find((c) => c.id === v.id)?.target ?? '';
      if (v.action === 'test')
        setMessage({ kind: 'success', text: t('notifications.testSent', { target }) });
      void refresh();
    },
    // Switching a recipient back on counts against the plan like adding one (SP-A2.54). A refusal
    // may come from a list that is out of date (a smaller plan switched a recipient off): reload it.
    onError: (error) => {
      setMessage({
        kind: 'error',
        text: switchOnRefusal(error) ?? errorMessage(error, t),
      });
      void refresh();
    },
  });
  const relink = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/alert-channels/{channelId}/telegram-link', {
          params: { path: { channelId: id } },
        }),
      ),
    onSuccess: (data, id) => onLinks({ channelId: id, ...data }),
    onError: (error) => setMessage({ kind: 'error', text: errorMessage(error, t) }),
  });

  if (channels.isPending) return <Loading />;
  if (channels.isError)
    return (
      <LoadError
        message={errorMessage(channels.error, t)}
        onRetry={() => void channels.refetch()}
      />
    );
  const { items, limit, chat } = channels.data;
  // Every channel takes a place, switched off or not, as the API counts when adding one.
  const full = items.length >= limit;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setMessage(undefined);
    if (!email.trim()) return setMessage({ kind: 'error', text: t('errors.email') });
    add.mutate();
  };
  return (
    <Card>
      <h2>
        {t('notifications.recipientsTitle')} <small>{t('notifications.recipientsIntro')}</small>
      </h2>
      <span className="limit">{t('notifications.limit', { count: items.length, max: limit })}</span>
      <ul className="rcpt" aria-label={t('notifications.recipientsTitle')}>
        {items.map((c) => (
          <Recipient
            key={c.id}
            channel={c}
            telegram={chat.telegram}
            onAct={(action, id) => act.mutate({ action, id })}
            onRelink={(id) => relink.mutate(id)}
            relinking={relink.isPending && relink.variables === c.id}
          />
        ))}
      </ul>
      {full ? (
        <Notice kind="info" title={t('notifications.limitReached', { count: limit })}>
          {t('notifications.limitHint')}
        </Notice>
      ) : (
        <form onSubmit={submit} noValidate>
          <div className="fields">
            <TextField
              label={t('notifications.addEmail')}
              type="email"
              autoComplete="off"
              placeholder="name@firma.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="field">
              <span aria-hidden="true" />
              <Button type="submit" variant="secondary" busy={add.isPending}>
                {t('notifications.add')}
              </Button>
            </div>
          </div>
        </form>
      )}
      {/* Below the form, where the address was typed (notifications.html). */}
      {message ? (
        <Notice kind={message.kind} title={message.title}>
          {message.text}
        </Notice>
      ) : null}
    </Card>
  );
}

/** S9 / US-07 (notifications.html): when we alert, who gets it, and the chat channels. */
export function Notifications() {
  const { t } = useTranslation();
  // One-time Telegram links: asked for from a waiting chat's row, shown in the Telegram box.
  const [links, setLinks] = useState<TelegramLinks>();
  return (
    <div className="page">
      <div className="page-title">
        <h1>{t('notifications.title')}</h1>
      </div>
      <div className="acct">
        <Preferences />
        <Recipients onLinks={setLinks} />
        <ChatChannelsCard links={links} onLinks={setLinks} />
      </div>
    </div>
  );
}
